import { useState, useMemo, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Gallery } from "./Gallery";
import { BookingModal } from "./BookingModal";
import { CABIN_IMAGES } from "@/lib/data";
import { useT } from "@/lib/i18n";
import { Check, CalendarDays, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { ro, enUS } from "date-fns/locale";
import { format, differenceInCalendarDays, eachDayOfInterval } from "date-fns";
import { fetchBlockedDates } from "@/lib/api";

const formatDate = (d, lang) =>
  format(d, "dd MMM yyyy", { locale: lang === "ro" ? ro : enUS });

export const CabinSection = ({ cabinKey, lang }) => {
  const t = useT(lang);
  const [range, setRange] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]); // array of Date objects

  useEffect(() => {
    fetchBlockedDates(cabinKey)
      .then((iso) => {
        setBlockedDates(
          iso.map((s) => {
            const [y, m, d] = s.split("-").map(Number);
            return new Date(y, m - 1, d);
          })
        );
      })
      .catch(() => {});
  }, [cabinKey]);

  // Validate that no booked date falls inside the picked range
  const validateRange = (next) => {
    if (next?.from && next?.to && blockedDates.length) {
      const days = eachDayOfInterval({ start: next.from, end: next.to });
      const collision = days.some((d) =>
        blockedDates.some(
          (b) =>
            b.getFullYear() === d.getFullYear() &&
            b.getMonth() === d.getMonth() &&
            b.getDate() === d.getDate()
        )
      );
      if (collision) {
        toast.error(
          lang === "ro"
            ? "Perioada conține zile deja ocupate."
            : "Selected range contains booked dates."
        );
        return;
      }
    }
    setRange(next);
  };

  const isS1 = cabinKey === "S1";
  const images = CABIN_IMAGES[cabinKey];
  const name = isS1 ? t.cabin.s1_name : t.cabin.s2_name;
  const tag = isS1 ? t.cabin.s1_tag : t.cabin.s2_tag;
  const desc = isS1 ? t.cabin.s1_desc : t.cabin.s2_desc;
  const features = isS1 ? t.features.s1 : t.features.s2;

  const nights =
    range?.from && range?.to
      ? Math.max(1, differenceInCalendarDays(range.to, range.from))
      : 0;

  const message = useMemo(() => {
    if (!range?.from || !range?.to) return "";
    return t.msgTemplate(
      cabinKey,
      formatDate(range.from, lang),
      formatDate(range.to, lang)
    );
  }, [range, cabinKey, lang, t]);

  const handleContact = () => {
    if (!range?.from || !range?.to) {
      toast.error(t.cabin.pleasePick);
      return;
    }
    setOpen(true);
  };

  return (
    <div
      data-testid={`cabin-section-${cabinKey}`}
      className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start"
    >
      {/* Left: gallery + description */}
      <div className="lg:col-span-3 space-y-8">
        <div>
          <div className="flex items-baseline gap-3 mb-2">
            <h3
              data-testid={`cabin-${cabinKey}-name`}
              className="font-serif-display text-3xl sm:text-4xl text-[#4a3520]"
            >
              {name}
            </h3>
            <span className="text-xs tracking-widest uppercase text-[#8a6a44]">
              {tag}
            </span>
          </div>
          <p className="text-[#5a4530] leading-relaxed max-w-2xl">{desc}</p>
        </div>

        <Gallery images={images} cabinKey={cabinKey} />

        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-[#8a6a44] mb-4">
            {t.cabin.features}
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {features.map((f) => (
              <div
                key={f}
                className="flex items-center gap-3 text-[#3d2a1b] text-sm"
              >
                <span className="w-7 h-7 rounded-full bg-[#efe5d3] border border-[#a17a4c]/40 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-[#8a6a3f]" />
                </span>
                {f}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right: calendar / booking */}
      <div className="lg:col-span-2 lg:sticky lg:top-24">
        <Card
          data-testid={`cabin-${cabinKey}-calendar-card`}
          className="bg-[#fbf6ea] border-[#a17a4c]/30 shadow-[0_8px_30px_-12px_rgba(43,29,18,0.25)] rounded-2xl overflow-hidden"
        >
          <div className="p-6 border-b border-[#a17a4c]/20 bg-[#f3e8d0]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#8a6a44] mb-1">
              <CalendarDays className="w-3.5 h-3.5" />
              {t.cabin.availability}
            </div>
            <h4 className="font-serif-display text-xl text-[#4a3520]">
              {t.cabin.pickDates}
            </h4>
            <p className="text-xs text-[#5a4530] mt-1">{t.cabin.pickHint}</p>
          </div>

          <div className="p-3 flex justify-center">
            <Calendar
              data-testid={`cabin-${cabinKey}-calendar`}
              mode="range"
              selected={range}
              onSelect={validateRange}
              numberOfMonths={1}
              locale={lang === "ro" ? ro : enUS}
              disabled={[{ before: new Date() }, ...blockedDates]}
              modifiers={{ booked: blockedDates }}
              modifiersStyles={{
                booked: {
                  backgroundColor: "rgba(180, 40, 40, 0.15)",
                  color: "#9b2c2c",
                  textDecoration: "line-through",
                },
              }}
              className="bg-transparent"
            />
          </div>

          <div className="p-5 border-t border-[#a17a4c]/20 space-y-4 bg-[#fbf6ea]">
            {range?.from && range?.to ? (
              <div
                data-testid={`cabin-${cabinKey}-selected`}
                className="rounded-lg bg-[#efe5d3] border border-[#a17a4c]/30 p-3"
              >
                <div className="text-[10px] uppercase tracking-widest text-[#8a6a44]">
                  {t.cabin.selected}
                </div>
                <div className="flex items-center justify-between mt-1">
                  <div className="text-sm font-medium text-[#4a3520]">
                    {formatDate(range.from, lang)}
                    <ArrowRight className="w-3.5 h-3.5 inline mx-2 text-[#8a6a44]" />
                    {formatDate(range.to, lang)}
                  </div>
                  <div className="text-xs text-[#5a4530]">
                    {nights} {nights === 1 ? t.cabin.night : t.cabin.nights}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed border-[#a17a4c]/40 p-3 text-center text-xs text-[#8a6a44]">
                {t.cabin.pickHint}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                data-testid={`cabin-${cabinKey}-contact-btn`}
                onClick={handleContact}
                className="flex-1 bg-[#8a6a3f] hover:bg-[#7a5d38] text-[#f7f1e6] h-11 rounded-full"
              >
                {t.cabin.contactNow}
              </Button>
              {range?.from && (
                <Button
                  data-testid={`cabin-${cabinKey}-reset-btn`}
                  variant="outline"
                  onClick={() => setRange(undefined)}
                  className="border-[#a17a4c]/40 text-[#4a3520] hover:bg-[#efe5d3] rounded-full h-11"
                >
                  {t.cabin.reset}
                </Button>
              )}
            </div>
          </div>
        </Card>
      </div>

      <BookingModal
        open={open}
        onOpenChange={setOpen}
        lang={lang}
        cabinName={name}
        message={message}
      />
    </div>
  );
};
